import { supabase } from '../supabaseClient';
import type { AnnualBehaviorNote, AnnualBehaviorNoteUpsert } from '../types/domain';
import { canWriteSchoolScopedDataCache, notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { setAnnualBehaviorNotesQueryData } from '../query/cacheUpdates';
import { emitAnnualBehaviorNotesSaved } from '../utils/annualBehaviorNotesSavedEvents';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

const ANNUAL_BEHAVIOR_NOTES_CACHE_VERSION = 'v1';

/** `cp.` + 워크스페이스 ID 세그먼트 → 워크스페이스 버전 sync 시 자동 purge 대상 */
function annualBehaviorNotesCacheKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `cp.annualBehaviorNotes.${ANNUAL_BEHAVIOR_NOTES_CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function sortNotes(rows: AnnualBehaviorNote[]): AnnualBehaviorNote[] {
  return [...rows].sort(
    (a, b) =>
      a.lesson_date.localeCompare(b.lesson_date) || (a.day_row_order ?? 0) - (b.day_row_order ?? 0)
  );
}

/** 행동특성 학기 캐시(동기). 없으면 null */
export function loadAnnualBehaviorNotesCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): AnnualBehaviorNote[] | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(
      annualBehaviorNotesCacheKey(workspaceId, academicYear, termNumber, gradeId)
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((row): row is AnnualBehaviorNote => !!row && typeof row === 'object');
  } catch {
    return null;
  }
}

function saveAnnualBehaviorNotesCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: AnnualBehaviorNote[]
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  if (!canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    deviceCacheStorage.setItem(
      annualBehaviorNotesCacheKey(workspaceId, academicYear, termNumber, gradeId),
      JSON.stringify(sortNotes(rows))
    );
  } catch {
    // ignore localStorage errors
  }
}

export function clearAnnualBehaviorNotesCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(
      annualBehaviorNotesCacheKey(workspaceId, academicYear, termNumber, gradeId)
    );
  } catch {
    // ignore localStorage errors
  }
}

/** 저장/삭제 성공 시 캐시를 제자리 갱신해 재조회 네트워크를 피한다 */
function mergeNoteIntoCache(note: AnnualBehaviorNote): void {
  const cached = loadAnnualBehaviorNotesCache(
    note.workspace_id,
    note.academic_year,
    note.term_number,
    note.grade_id
  );
  if (cached == null) return;
  const next = cached.filter(
    (row) => !(row.lesson_date === note.lesson_date && (row.day_row_order ?? 0) === (note.day_row_order ?? 0))
  );
  next.push(note);
  saveAnnualBehaviorNotesCache(note.workspace_id, note.academic_year, note.term_number, note.grade_id, next);
}

function removeNoteFromCache(input: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  lessonDate: string;
  dayRowOrder: number;
}): void {
  const cached = loadAnnualBehaviorNotesCache(
    input.workspaceId,
    input.academicYear,
    input.termNumber,
    input.gradeId
  );
  if (cached == null) return;
  const next = cached.filter(
    (row) => !(row.lesson_date === input.lessonDate && (row.day_row_order ?? 0) === input.dayRowOrder)
  );
  saveAnnualBehaviorNotesCache(input.workspaceId, input.academicYear, input.termNumber, input.gradeId, next);
}

const ANNUAL_BEHAVIOR_NOTES_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'academic_year',
  'term_number',
  'lesson_date',
  'day_row_order',
  'behavior_note',
  'record_member_group_id',
  'source_annual_cell_key_snapshot',
  'period_label_snapshot',
  'timetable_text_snapshot',
  'created_at',
  'updated_at',
].join(', ');

/** ai_feedback 컬럼(20261017200000 마이그레이션) 미적용 DB 호환: 감지 시 컬럼 없이 재시도 */
let aiFeedbackColumnMissing = false;
const selectList = () =>
  aiFeedbackColumnMissing ? ANNUAL_BEHAVIOR_NOTES_SELECT : `${ANNUAL_BEHAVIOR_NOTES_SELECT}, ai_feedback`;
function isMissingAiFeedbackColumn(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  const m = (error.message ?? '').toLowerCase();
  return m.includes('ai_feedback') && (m.includes('column') || m.includes('schema cache') || error.code === '42703' || error.code === 'PGRST204');
}
export function isAnnualBehaviorAiFeedbackColumnMissing(): boolean {
  return aiFeedbackColumnMissing;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function t(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

function sanitizeAnnualBehaviorNoteUpsert(row: AnnualBehaviorNoteUpsert): AnnualBehaviorNoteUpsert {
  const memberGroupRaw = t(row.record_member_group_id ?? undefined);
  const memberGroup =
    !memberGroupRaw || memberGroupRaw === 'null' || memberGroupRaw === 'undefined' ? '' : memberGroupRaw;
  const dayRowOrderRaw = Number.isInteger(row.day_row_order) ? row.day_row_order : 0;
  return {
    ...row,
    lesson_date: t(row.lesson_date),
    day_row_order: Math.max(0, dayRowOrderRaw),
    behavior_note: row.behavior_note ?? '',
    record_member_group_id: memberGroup ? memberGroup : null,
    source_annual_cell_key_snapshot: t(row.source_annual_cell_key_snapshot ?? undefined),
    period_label_snapshot: t(row.period_label_snapshot ?? undefined),
    timetable_text_snapshot: t(row.timetable_text_snapshot ?? undefined),
    // 기록이 비면 피드백도 비움. undefined 는 키 자체를 빼서 기존 값 유지
    ...(row.ai_feedback !== undefined || !t(row.behavior_note)
      ? { ai_feedback: t(row.behavior_note) ? row.ai_feedback ?? '' : '' }
      : {}),
  };
}

export async function getAnnualBehaviorNotes(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  options?: { forceRefresh?: boolean }
): Promise<AnnualBehaviorNote[]> {
  if (!workspaceId || !gradeId) return [];
  if (!options?.forceRefresh) {
    const cached = loadAnnualBehaviorNotesCache(workspaceId, academicYear, termNumber, gradeId);
    if (cached != null) {
      recordDevLocalStorageProgramCacheUse('행동특성·LS');
      return cached;
    }
  }
  const run = () =>
    supabase
      .from('annual_behavior_notes')
      .select(selectList())
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .order('lesson_date', { ascending: true })
      .order('day_row_order', { ascending: true });
  let { data, error } = await run();
  if (error && !aiFeedbackColumnMissing && isMissingAiFeedbackColumn(error)) {
    aiFeedbackColumnMissing = true;
    ({ data, error } = await run());
  }
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown) as AnnualBehaviorNote[];
  saveAnnualBehaviorNotesCache(workspaceId, academicYear, termNumber, gradeId, rows);
  setAnnualBehaviorNotesQueryData(workspaceId, academicYear, termNumber, gradeId, rows);
  return rows;
}

export async function upsertAnnualBehaviorNote(row: AnnualBehaviorNoteUpsert): Promise<AnnualBehaviorNote | null> {
  const note = sanitizeAnnualBehaviorNoteUpsert(row);
  if (
    !note.workspace_id ||
    !note.grade_id ||
    !ISO_DATE_RE.test(note.lesson_date) ||
    !Number.isFinite(note.academic_year as number) ||
    !(note.term_number === 1 || note.term_number === 2)
  ) {
    return null;
  }
  const buildPayload = () => {
    const p: AnnualBehaviorNoteUpsert & { updated_at: string } = { ...note, updated_at: new Date().toISOString() };
    if (aiFeedbackColumnMissing) delete p.ai_feedback;
    return p;
  };
  const run = () =>
    supabase
      .from('annual_behavior_notes')
      .upsert(buildPayload(), {
        onConflict: 'workspace_id,grade_id,academic_year,term_number,lesson_date,day_row_order',
      })
      .select(selectList())
      .limit(1);
  let { data, error } = await run();
  if (error && !aiFeedbackColumnMissing && isMissingAiFeedbackColumn(error)) {
    // 컬럼 미적용: 기록 저장은 그대로 진행(피드백만 저장 안 됨)
    aiFeedbackColumnMissing = true;
    ({ data, error } = await run());
  }
  if (error) throw new Error(error.message);
  const saved =
    (((data ?? []) as unknown as AnnualBehaviorNote[])[0] as AnnualBehaviorNote | undefined) ?? null;
  if (saved) {
    mergeNoteIntoCache(saved);
    const cached = loadAnnualBehaviorNotesCache(
      saved.workspace_id,
      saved.academic_year,
      saved.term_number,
      saved.grade_id
    );
    if (cached != null) {
      setAnnualBehaviorNotesQueryData(
        saved.workspace_id,
        saved.academic_year,
        saved.term_number,
        saved.grade_id,
        cached
      );
    }
    emitAnnualBehaviorNotesSaved({
      workspaceId: saved.workspace_id,
      academicYear: saved.academic_year,
      termNumber: saved.term_number,
      gradeId: saved.grade_id,
    });
  }
  await notifySchoolProgramMutation(note.workspace_id, ['behaviorNotes']);
  return saved;
}

export async function deleteAnnualBehaviorNoteByNaturalKey(input: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  lessonDate: string;
  dayRowOrder: number;
}): Promise<void> {
  const lessonDate = t(input.lessonDate);
  const rowOrder = Number.isInteger(input.dayRowOrder) ? input.dayRowOrder : 0;
  if (!input.workspaceId || !input.gradeId || !ISO_DATE_RE.test(lessonDate)) return;
  const { error } = await supabase
    .from('annual_behavior_notes')
    .delete()
    .eq('workspace_id', input.workspaceId)
    .eq('grade_id', input.gradeId)
    .eq('academic_year', input.academicYear)
    .eq('term_number', input.termNumber)
    .eq('lesson_date', lessonDate)
    .eq('day_row_order', Math.max(0, rowOrder));
  if (error) throw new Error(error.message);
  removeNoteFromCache({ ...input, lessonDate, dayRowOrder: Math.max(0, rowOrder) });
  const cached = loadAnnualBehaviorNotesCache(
    input.workspaceId,
    input.academicYear,
    input.termNumber,
    input.gradeId
  );
  setAnnualBehaviorNotesQueryData(
    input.workspaceId,
    input.academicYear,
    input.termNumber,
    input.gradeId,
    cached ?? []
  );
  emitAnnualBehaviorNotesSaved({
    workspaceId: input.workspaceId,
    academicYear: input.academicYear,
    termNumber: input.termNumber,
    gradeId: input.gradeId,
  });
  await notifySchoolProgramMutation(input.workspaceId, ['behaviorNotes']);
}
